import { useState } from "react";
import { Home, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Kicker } from "@/components/Bits";
import { Field, inputCls, useLeadSubmit } from "@/components/FormKit";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const triggerCls =
  "border-0 border-b border-foreground/25 rounded-none px-0 h-auto py-3 text-lg focus:ring-0 focus:border-primary";

const sellerEmpty = {
  name: "", phone: "", email: "", property_address: "", year: "", bedrooms: "", bathrooms: "",
  size: "", condition: "", placement: "", move_status: "", timeline: "", message: "",
};
const buyerEmpty = {
  name: "", phone: "", email: "", investor_type: "", markets: "", property_interest: "",
  budget: "", financing: "Cash", message: "",
};

function SellerForm() {
  const [form, setForm] = useState(sellerEmpty);
  const { submit, submitting } = useLeadSubmit("mobile_home_seller");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const onSubmit = (e) => { e.preventDefault(); submit(form, () => setForm(sellerEmpty)); };

  return (
    <form data-testid="seller-form" onSubmit={onSubmit} className="rounded-3xl border border-border bg-background p-7 sm:p-10">
      <h3 className="font-display text-3xl tracking-tight">Tell us about your mobile home</h3>
      <div className="mt-8 grid gap-7 sm:grid-cols-2">
        <Field label="Name" htmlFor="s-name">
          <input id="s-name" data-testid="s-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
        </Field>
        <Field label="Phone" htmlFor="s-phone">
          <input id="s-phone" data-testid="s-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" />
        </Field>
        <Field label="Email" htmlFor="s-email">
          <input id="s-email" data-testid="s-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </Field>
        <Field label="Property Location" htmlFor="s-loc">
          <input id="s-loc" data-testid="s-loc" className={inputCls} value={form.property_address} onChange={set("property_address")} placeholder="City, County, SC" />
        </Field>
        <Field label="Year of Home" htmlFor="s-year">
          <input id="s-year" data-testid="s-year" className={inputCls} value={form.year} onChange={set("year")} placeholder="e.g. 1998" />
        </Field>
        <Field label="Approx. Size (sq ft)" htmlFor="s-size">
          <input id="s-size" data-testid="s-size" className={inputCls} value={form.size} onChange={set("size")} placeholder="e.g. 1,100" />
        </Field>
        <Field label="Bedrooms" htmlFor="s-beds">
          <input id="s-beds" data-testid="s-beds" className={inputCls} value={form.bedrooms} onChange={set("bedrooms")} placeholder="e.g. 3" />
        </Field>
        <Field label="Bathrooms" htmlFor="s-baths">
          <input id="s-baths" data-testid="s-baths" className={inputCls} value={form.bathrooms} onChange={set("bathrooms")} placeholder="e.g. 2" />
        </Field>
        <Field label="Current Condition">
          <Select value={form.condition} onValueChange={set("condition")}>
            <SelectTrigger data-testid="s-condition" className={triggerCls}><SelectValue placeholder="Select condition" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Excellent">Excellent</SelectItem>
              <SelectItem value="Good">Good</SelectItem>
              <SelectItem value="Fair">Fair</SelectItem>
              <SelectItem value="Needs Work">Needs Work</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Placement">
          <Select value={form.placement} onValueChange={set("placement")}>
            <SelectTrigger data-testid="s-placement" className={triggerCls}><SelectValue placeholder="Where is it located?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="In a mobile home park">In a mobile home park</SelectItem>
              <SelectItem value="On private property">On private property</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Will it stay or be moved?">
          <Select value={form.move_status} onValueChange={set("move_status")}>
            <SelectTrigger data-testid="s-move" className={triggerCls}><SelectValue placeholder="Select an option" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Staying in place">Staying in place</SelectItem>
              <SelectItem value="Needs to be moved">Needs to be moved</SelectItem>
              <SelectItem value="Not sure">Not sure</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Selling Timeline">
          <Select value={form.timeline} onValueChange={set("timeline")}>
            <SelectTrigger data-testid="s-timeline" className={triggerCls}><SelectValue placeholder="How soon?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="As soon as possible">As soon as possible</SelectItem>
              <SelectItem value="1–3 months">1–3 months</SelectItem>
              <SelectItem value="3–6 months">3–6 months</SelectItem>
              <SelectItem value="Just exploring">Just exploring</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Tell us a little more" className="mt-7">
        <Textarea data-testid="s-message" value={form.message} onChange={set("message")}
          placeholder="Anything we should know — lot rent, repairs, photos you can share, etc."
          className="bg-transparent border border-foreground/20 rounded-xl min-h-28 text-base focus-visible:ring-primary" />
      </Field>
      <Button type="submit" data-testid="s-submit" disabled={submitting}
        className="mt-9 w-full rounded-full bg-primary text-primary-foreground py-6 text-base font-semibold hover:bg-primary/90 hover:scale-[1.01] transition-transform">
        {submitting ? "Sending..." : "Get My Mobile Home Offer"}
      </Button>
    </form>
  );
}

function BuyerForm() {
  const [form, setForm] = useState(buyerEmpty);
  const { submit, submitting } = useLeadSubmit("buyer_investor");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const onSubmit = (e) => { e.preventDefault(); submit(form, () => setForm(buyerEmpty)); };

  return (
    <form data-testid="buyer-form" onSubmit={onSubmit} className="rounded-3xl border border-border bg-background p-7 sm:p-10">
      <h3 className="font-display text-3xl tracking-tight">Join our buyers & investors list</h3>
      <p className="mt-2 text-foreground/60">Tell us what you're looking for and we'll reach out when a match comes up.</p>
      <div className="mt-8 grid gap-7 sm:grid-cols-2">
        <Field label="Name" htmlFor="b-name">
          <input id="b-name" data-testid="b-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
        </Field>
        <Field label="Phone" htmlFor="b-phone">
          <input id="b-phone" data-testid="b-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" />
        </Field>
        <Field label="Email" htmlFor="b-email">
          <input id="b-email" data-testid="b-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </Field>
        <Field label="I am a...">
          <Select value={form.investor_type} onValueChange={set("investor_type")}>
            <SelectTrigger data-testid="b-type" className={triggerCls}><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Individual Buyer">Individual Buyer</SelectItem>
              <SelectItem value="Investor">Investor</SelectItem>
              <SelectItem value="Mobile Home Dealer">Mobile Home Dealer</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Markets / Areas of Interest" htmlFor="b-markets">
          <input id="b-markets" data-testid="b-markets" className={inputCls} value={form.markets} onChange={set("markets")} placeholder="e.g. Columbia, Charleston, SC" />
        </Field>
        <Field label="Property Interest">
          <Select value={form.property_interest} onValueChange={set("property_interest")}>
            <SelectTrigger data-testid="b-interest" className={triggerCls}><SelectValue placeholder="What are you after?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="In-park homes">In-park homes</SelectItem>
              <SelectItem value="Homes on private land">Homes on private land</SelectItem>
              <SelectItem value="Homes needing repair">Homes needing repair</SelectItem>
              <SelectItem value="Any">Any / Open</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Budget Range">
          <Select value={form.budget} onValueChange={set("budget")}>
            <SelectTrigger data-testid="b-budget" className={triggerCls}><SelectValue placeholder="Select range" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Under $10k">Under $10k</SelectItem>
              <SelectItem value="$10k–$25k">$10k–$25k</SelectItem>
              <SelectItem value="$25k–$50k">$25k–$50k</SelectItem>
              <SelectItem value="$50k+">$50k+</SelectItem>
              <SelectItem value="Flexible">Flexible</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Financing">
          <RadioGroup data-testid="b-financing" value={form.financing} onValueChange={set("financing")} className="flex flex-wrap gap-3 pt-2">
            {["Cash", "Financing"].map((m) => (
              <Label key={m} htmlFor={`fin-${m}`}
                className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                  form.financing === m ? "border-primary bg-primary text-primary-foreground" : "border-foreground/25 text-foreground/70 hover:border-primary"
                }`}>
                <RadioGroupItem id={`fin-${m}`} value={m} className="sr-only" />
                {m}
              </Label>
            ))}
          </RadioGroup>
        </Field>
      </div>
      <Field label="Notes" className="mt-7">
        <Textarea data-testid="b-message" value={form.message} onChange={set("message")}
          placeholder="Anything specific you're looking for?"
          className="bg-transparent border border-foreground/20 rounded-xl min-h-28 text-base focus-visible:ring-primary" />
      </Field>
      <Button type="submit" data-testid="b-submit" disabled={submitting}
        className="mt-9 w-full rounded-full bg-primary text-primary-foreground py-6 text-base font-semibold hover:bg-primary/90 hover:scale-[1.01] transition-transform">
        {submitting ? "Sending..." : "Join the Buyers List"}
      </Button>
    </form>
  );
}

export const AboutLeadForms = () => {
  const [mode, setMode] = useState("seller");
  const tabs = [
    { key: "seller", label: "I'm Selling a Mobile Home", Icon: Home },
    { key: "buyer", label: "I'm a Buyer / Investor", Icon: Users },
  ];
  return (
    <section id="start" data-testid="lead-forms" className="border-y border-border bg-secondary/40">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <Kicker>Get Started</Kicker>
          <h2 className="font-display text-4xl sm:text-6xl tracking-mega leading-[0.9] mt-5">
            Tell us a little about your situation.
          </h2>
          <p className="mt-6 text-lg text-foreground/75 max-w-2xl">
            No pressure. No obligation. Just options. Choose the path that fits you best.
          </p>

          <div className="mt-10 inline-flex flex-wrap gap-2 rounded-full bg-background border border-border p-1.5">
            {tabs.map((t) => (
              <button
                key={t.key}
                data-testid={`lead-tab-${t.key}`}
                onClick={() => setMode(t.key)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${
                  mode === t.key ? "bg-primary text-primary-foreground" : "text-foreground/65 hover:text-foreground"
                }`}
              >
                <t.Icon size={16} /> {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10">{mode === "seller" ? <SellerForm /> : <BuyerForm />}</div>
        </Reveal>
      </div>
    </section>
  );
};
